import ReactECharts from "echarts-for-react";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import {
 
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import React, { useState, useEffect } from "react";
import Layout from "../../../components/layout/layout";
import { Card } from "../../../components/common/Card";
import QuickAccess from "../../../assets/dashboard/quickAccess.svg";
import QuickExpert from "../../../assets/dashboard/quickExpert.svg";
import QuickAnswer from "../../../assets/dashboard/quickAnswer.svg";
import Interaction from "../../../assets/dashboard/interaction.svg";
import AmountSpend from "../../../assets/dashboard/amountSpend.svg";
import ExperttieSmall from "../../../assets/dashboard/ExperttieSmall.png";
import videoIconSmall from "../../../assets/dashboard/videoIconSmall.png";
import tooltipSmall from "../../../assets/dashboard/tooltipSmall.png";
import { CardVideo } from "../../../components/Card";
import DateRangePicker from "../../../components/DateRange/DateRangePicker";
import setUpTeamsImg from "../../../assets/image/setUpTeams.png";
import DownloadExtension from "../../../common/Dashboard/DownloadExtentionCard";
import QuickSupportCard from "../../../common/Dashboard/QuickSupportCard";
import SetUpTeamCard from "../../../common/Dashboard/SetUpTeamCard";
import InteractionsCard from "../../../components/Dashboard/InteractionsCard";
import ChartCard, {
  CircularChart,
  StatsCounter,
} from "../../../components/Dashboard/ChartCard";
import BarChart from "../../../components/Dashboard/BarChart";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../../store";
import { useAppState } from "../../../hooks/useAppState";
import { fetchQuickAccessCounts, fetchChatSessions } from "../../../store/profileSlice";
import { fetchActivitiesByRole } from "../../../store/slices/activitySlice";
import {
  fetchAllTeamData,
  filterMembersTeams,
} from "../../../store/slices/teamSlice";
import { useAppSelector } from "../../../store/hooks";
import { fetchTooltipPurchases } from "../../../store/slices/tooltipSlice";

const accessData = [
  { name: "Help Center", clicks: 53, color: "bg-[#F15D18]" },
  { name: "Support Chat", clicks: 17, color: "bg-[#F58957]" },
  { name: "Video Tutorials", clicks: 30, color: "bg-[#9F390A]" },
  { name: "Tooltip Package", clicks: 96, color: "bg-[#919191]" },
];
const maxClicks = Math.max(...accessData.map((item) => item.clicks));
const CustomerDashboard = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState<{
    start: string | null;
    end: string | null;
  }>({
    start: null,
    end: null,
  });
  const profileData = useAppSelector((state: RootState) => state.profile.user);
  const { filteredMembers, members, teams, status, currentTeam } = useSelector(
    (state: any) => state.team
  );
  const [timeframeDropdownOpen, setTimeframeDropdownOpen] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState("");
  const timeframeRef = React.useRef<HTMLDivElement | null>(null);
  const datePickerRef = React.useRef<HTMLDivElement | null>(null);
  const [statsDropdownOpen, setStatsDropdownOpen] = useState(false);
  const [selectedStatsOption, setSelectedStatsOption] = useState("Me");
  const { user } = useAppState();
  const chatSessions = useSelector((state: RootState) => state.profile.chatSessions || []);
  // Use activitySlice for activities
  const allActivities = useSelector((state: RootState) => state.activity.customerActivities || []);
  // Use quickAccessCounts from Redux store (real-time managed)
  const quickAccessCounts = useSelector(
    (state: RootState) => state.profile.quickAccessCounts
  );
  // Use tooltip purchases from Redux store
  const { tooltipPurchases, totalTooltipSpent, loading: tooltipLoading } = useSelector(
    (state: RootState) => state.tooltip
  );
  // Helper function to filter activities by timeframe
  const getFilteredActivitiesByTimeframe = (
    activities: any[],
    timeframe: string
  ) => {
    if (!timeframe || !activities) return activities;

    const now = new Date();
    const filterDate = new Date();

    switch (timeframe) {
      case "this-month":
        filterDate.setMonth(now.getMonth(), 1);
        filterDate.setHours(0, 0, 0, 0);
        break;
      case "last-3-months":
        filterDate.setMonth(now.getMonth() - 3);
        filterDate.setHours(0, 0, 0, 0);
        break;
      case "last-12-months":
        filterDate.setFullYear(now.getFullYear() - 1);
        filterDate.setHours(0, 0, 0, 0);
        break;
      case "ytd":
        filterDate.setFullYear(now.getFullYear(), 0, 1);
        filterDate.setHours(0, 0, 0, 0);
        break;
      default:
        return activities;
    }

    return activities.filter((activity: any) => {
      // Handle Firestore timestamp objects and regular Date objects
      let activityDate = null;

      if (activity.createdAt) {
        activityDate = activity.createdAt.toDate
          ? activity.createdAt.toDate()
          : new Date(activity.createdAt);
      } else if (activity.startTime) {
        activityDate = activity.startTime.toDate
          ? activity.startTime.toDate()
          : new Date(activity.startTime);
      } else if (activity.timestamp) {
        activityDate = activity.timestamp.toDate
          ? activity.timestamp.toDate()
          : new Date(activity.timestamp);
      } else if (activity.date) {
        activityDate = new Date(activity.date);
      }

      return activityDate && activityDate >= filterDate;
    });
  };

  // Helper function to filter activities by custom date range
  const getFilteredActivitiesByDateRange = (
    activities: any[],
    dateRange: any
  ) => {
    if (!dateRange.start || !dateRange.end || !activities) return activities;

    const startDate = new Date(dateRange.start);
    const endDate = new Date(dateRange.end);
    endDate.setHours(23, 59, 59, 999); // Include the entire end date

    return activities.filter((activity: any) => {
      // Handle Firestore timestamp objects and regular Date objects
      let activityDate = null;

      if (activity.createdAt) {
        activityDate = activity.createdAt.toDate
          ? activity.createdAt.toDate()
          : new Date(activity.createdAt);
      } else if (activity.startTime) {
        activityDate = activity.startTime.toDate
          ? activity.startTime.toDate()
          : new Date(activity.startTime);
      } else if (activity.timestamp) {
        activityDate = activity.timestamp.toDate
          ? activity.timestamp.toDate()
          : new Date(activity.timestamp);
      } else if (activity.date) {
        activityDate = new Date(activity.date);
      }

      return (
        activityDate && activityDate >= startDate && activityDate <= endDate
      );
    });
  };
  // Helper function to filter chat sessions by timeframe
  const filterChatSessionsByTimeframe = (
    sessions: any[],
    timeframe: string
  ) => {
    if (!timeframe || !sessions) return sessions;
    const now = new Date();
    const filterDate = new Date();
    switch (timeframe) {
      case "this-month":
        filterDate.setMonth(now.getMonth(), 1);
        filterDate.setHours(0, 0, 0, 0);
        break;
      case "last-3-months":
        filterDate.setMonth(now.getMonth() - 3);
        filterDate.setHours(0, 0, 0, 0);
        break;
      case "last-12-months":
        filterDate.setFullYear(now.getFullYear() - 1);
        filterDate.setHours(0, 0, 0, 0);
        break;
      case "ytd":
        filterDate.setFullYear(now.getFullYear(), 0, 1);
        filterDate.setHours(0, 0, 0, 0);
        break;
      default:
        return sessions;
    }
    return sessions.filter((session: any) => {
      let sessionDate = null;
      if (session.createdAt) {
        sessionDate = session.createdAt.toDate
          ? session.createdAt.toDate()
          : new Date(session.createdAt);
      }
      return sessionDate && sessionDate >= filterDate;
    });
  };
  // Helper function to filter chat sessions by custom date range
  const filterChatSessionsByDateRange = (sessions: any[], dateRange: any) => {
    if (!dateRange.start || !dateRange.end || !sessions) return sessions;
    const startDate = new Date(dateRange.start);
    const endDate = new Date(dateRange.end);
    endDate.setHours(23, 59, 59, 999);
    return sessions.filter((session: any) => {
      let sessionDate = null;
      if (session.createdAt) {
        sessionDate = session.createdAt.toDate
          ? session.createdAt.toDate()
          : new Date(session.createdAt);
      }
      return sessionDate && sessionDate >= startDate && sessionDate <= endDate;
    });
  };

  // Helper function to filter tooltip purchases by timeframe
  const getFilteredTooltipPurchasesByTimeframe = (
    purchases: any[],
    timeframe: string
  ) => {
    if (!timeframe || !purchases) return purchases;

    const now = new Date();
    const filterDate = new Date();

    switch (timeframe) {
      case "this-month":
        filterDate.setMonth(now.getMonth(), 1);
        filterDate.setHours(0, 0, 0, 0);
        break;
      case "last-3-months":
        filterDate.setMonth(now.getMonth() - 3);
        filterDate.setHours(0, 0, 0, 0);
        break;
      case "last-12-months":
        filterDate.setFullYear(now.getFullYear() - 1);
        filterDate.setHours(0, 0, 0, 0);
        break;
      case "ytd":
        filterDate.setFullYear(now.getFullYear(), 0, 1);
        filterDate.setHours(0, 0, 0, 0);
        break;
      default:
        return purchases;
    }

    return purchases.filter((purchase: any) => {
      let purchaseDate = null;

      if (purchase.purchasedAt) {
        purchaseDate = purchase.purchasedAt.toDate
          ? purchase.purchasedAt.toDate()
          : new Date(purchase.purchasedAt);
      } else if (purchase.createdAt) {
        purchaseDate = purchase.createdAt.toDate
          ? purchase.createdAt.toDate()
          : new Date(purchase.createdAt);
      }

      return purchaseDate && purchaseDate >= filterDate;
    });
  };

  // Helper function to filter tooltip purchases by custom date range
  const getFilteredTooltipPurchasesByDateRange = (
    purchases: any[],
    dateRange: any
  ) => {
    if (!dateRange.start || !dateRange.end || !purchases) return purchases;

    const startDate = new Date(dateRange.start);
    const endDate = new Date(dateRange.end);
    endDate.setHours(23, 59, 59, 999);

    return purchases.filter((purchase: any) => {
      let purchaseDate = null;

      if (purchase.purchasedAt) {
        purchaseDate = purchase.purchasedAt.toDate
          ? purchase.purchasedAt.toDate()
          : new Date(purchase.purchasedAt);
      } else if (purchase.createdAt) {
        purchaseDate = purchase.createdAt.toDate
          ? purchase.createdAt.toDate()
          : new Date(purchase.createdAt);
      }

      return (
        purchaseDate && purchaseDate >= startDate && purchaseDate <= endDate
      );
    });
  };
  // Fetch activities when component mounts
  useEffect(() => {
    if (user && user.uid) {
      dispatch(
        fetchActivitiesByRole({ role: "Customer", userId: user.uid }) as any
      );
      // Only dispatch fetchQuickAccessCounts, do not use .then or set local state
      dispatch(fetchQuickAccessCounts(user.uid) as any);
      dispatch(fetchChatSessions(user.uid) as any);
      // Fetch tooltip purchases
      dispatch(fetchTooltipPurchases(user.uid) as any);
    }
  }, [dispatch, user]);
  // Fetch team data when component mounts
  useEffect(() => {
    const fetchTeamData = async () => {
      if (!user?.orgId || !user?.uid) {
        return;
      }

      try {
        // Dispatch the fetchAllTeamData action from the team slice
        await dispatch(
          fetchAllTeamData({
            orgId: user.orgId,
            userId: user.teamId || user.uid,
            currentId: user?.uid,
            userRole: user.uiRole || user?.teamRole,
          })
        ).unwrap();
      } catch (error) {
        console.error("Error fetching team data:", error);
      }
    };

    fetchTeamData();
  }, [dispatch, user?.orgId, user?.uid, user?.uiRole]);
  // Update filtered members when team data changes
  useEffect(() => {
    if (members?.length > 0 && user) {
      console.log("Dashboard - Updating filtered members:", {
        members,
        user: user.uiRole,
      });
      dispatch(
        filterMembersTeams({
          searchTerm: "",
          userRole: user.uiRole,
          userId: user.teamId || user.uid,
        })
      );
    }
  }, [dispatch, members, user]); // Recalculate filtered activities when timeframe or date range changes
  useEffect(() => {
    console.log("Filters changed:", {
      selectedTimeframe,
      dateRange,
      totalActivities: allActivities?.length || 0,
    });
  }, [selectedTimeframe, dateRange, allActivities]);
  // Filter and format activities for Quick Experts with timeframe and date range filtering
  const baseFilteredActivities = Array.isArray(allActivities)
    ? allActivities.filter(
        (activity: any) =>
          activity.type === "Video Call" ||
          activity.type === "Chat" ||
          activity.status === "completed" ||
          activity.status === "Issue reported" ||
          activity.status === "active"
      )
    : [];

  // Apply timeframe filter
  let timeFrameFilteredActivities = selectedTimeframe
    ? getFilteredActivitiesByTimeframe(
        baseFilteredActivities,
        selectedTimeframe
      )
    : baseFilteredActivities;

  // Apply date range filter if custom date range is set
  const filteredActivities =
    dateRange.start && dateRange.end
      ? getFilteredActivitiesByDateRange(timeFrameFilteredActivities, dateRange)
      : timeFrameFilteredActivities;

  // Filter chat sessions with timeframe and date range filtering
  const baseChatSessions = Array.isArray(chatSessions) ? chatSessions : [];

  // Apply timeframe filter to chat sessions
  let timeFrameFilteredChatSessions = selectedTimeframe
    ? filterChatSessionsByTimeframe(baseChatSessions, selectedTimeframe)
    : baseChatSessions;

  // Apply date range filter to chat sessions if custom date range is set
  const filteredChatSessions =
    dateRange.start && dateRange.end
      ? filterChatSessionsByDateRange(timeFrameFilteredChatSessions, dateRange)
      : timeFrameFilteredChatSessions;

  // Filter tooltip purchases with timeframe and date range filtering
  const baseTooltipPurchases = Array.isArray(tooltipPurchases) ? tooltipPurchases : [];

  // Apply timeframe filter to tooltip purchases
  const timeFrameFilteredTooltipPurchases = selectedTimeframe
    ? getFilteredTooltipPurchasesByTimeframe(
        baseTooltipPurchases,
        selectedTimeframe
      )
    : baseTooltipPurchases;

  // Apply date range filter to tooltip purchases if custom date range is set
  const filteredTooltipPurchases =
    dateRange.start && dateRange.end
      ? getFilteredTooltipPurchasesByDateRange(timeFrameFilteredTooltipPurchases, dateRange)
      : timeFrameFilteredTooltipPurchases;

  // Calculate total tooltip purchases amount
  const totalTooltipPurchasesAmount = filteredTooltipPurchases.reduce(
    (total: number, purchase: any) => {
      const price = typeof purchase.price === "number" ? purchase.price : parseFloat(purchase.price) || 0;
      return total + price;
    },
    0
  );

  // Calculate Quick Answers metrics
  const totalChatSessions = filteredChatSessions.length;
  const highRatedChatSessions = filteredChatSessions.filter((session: any) => {
    const rating = parseFloat(session.rating);
    return rating >= 4;
  });
   const totalChatSessionsWithRating = filteredChatSessions.filter((session: any) => session.rating).length;

  const quickAnswersResolutionScore =
    totalChatSessions > 0
      ? Math.round((highRatedChatSessions.length / totalChatSessionsWithRating) * 100)
      : 0;

  // Debug log for filteredMembers
  useEffect(() => {
    console.log("Dashboard - Current filteredMembers:", filteredMembers);
    console.log(
      "Dashboard - Current activeFilteredMembers (excluding invited):",
      filteredMembers?.filter((member: any) => member.status !== "invited")
    );
    console.log("Dashboard - Current teams:", teams);
    console.log("Dashboard - Current members:", members);
  }, [filteredMembers, teams, members]);
  // Calculate total amount spent from activities
  const totalSessionsAmount = filteredActivities.reduce(
    (total: number, activity: any) => {
      // Try multiple ways to extract the amount based on the database structure
      let amount = 0;

      // Check for cost field (as shown in the database sample)
      if (activity.cost !== undefined && activity.cost !== null) {
        amount =
          typeof activity.cost === "string"
            ? parseFloat(activity.cost.replace(/[$,]/g, ""))
            : parseFloat(activity.cost);
      }
      // Fallback to other possible amount fields
      else if (activity.amount !== undefined && activity.amount !== null) {
        amount =
          typeof activity.amount === "string"
            ? parseFloat(activity.amount.replace(/[$,]/g, ""))
            : parseFloat(activity.amount);
      }
      // Check metadata for amount
      else if (
        activity.metadata?.amount !== undefined &&
        activity.metadata?.amount !== null
      ) {
        amount =
          typeof activity.metadata.amount === "string"
            ? parseFloat(activity.metadata.amount.replace(/[$,]/g, ""))
            : parseFloat(activity.metadata.amount);
      }

      return total + (isNaN(amount) ? 0 : amount);
    },
    0
  ); // Calculate satisfaction score from expert ratings
  const highRatedSessions = filteredActivities.filter((activity: any) => {
    const rating = parseFloat(activity.customerRating);
    return rating >= 4;
  });

  const satisfactionScore =
    filteredActivities.length > 0
      ? Math.round((highRatedSessions.length / filteredActivities.length) * 100)
      : 0;

  // Filter out invited users for stats dropdown and active team member count
  const activeFilteredMembers =
    filteredMembers && filteredMembers.length > 0
      ? filteredMembers.filter((member: any) => member.status !== "invited")
      : [];

  function formatRole(role) {
    switch (role) {
      case "customer_owner":
        return "Team Owner";
      case "customer_team_lead":
        return "Team Lead";
      case "customer_team_member":
        return "Team Member";
      default:
        return "Customer";
    }
  }

  const statsOptions = [
    { value: "Me", label: "Me" },
    { value: "All", label: "All" },
    ...activeFilteredMembers.map((member: any) => ({
      value: member.id || member.uid,
      label: `${member.name} (${formatRole(member.role)})`,
    })),
  ];
  const timeframeOptions = [
    { value: "", label: "All time" },
    { value: "this-month", label: "This month" },
    { value: "last-3-months", label: "Last 3 months" },
    { value: "last-12-months", label: "Last 12 months" },
    { value: "ytd", label: "Year to date" },
  ];
  const handleTimeframeSelect = (value: string) => {
    setSelectedTimeframe(value);
    setTimeframeDropdownOpen(false);
    // Clear custom date range when timeframe is selected
    if (value) {
      setDateRange({ start: null, end: null });
    }
  };

  // Close timeframe dropdown when clicking outside
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (timeframeRef.current && !timeframeRef.current.contains(target)) {
        setTimeframeDropdownOpen(false);
      }
    };

    if (timeframeDropdownOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('touchstart', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [timeframeDropdownOpen]);

  // Close date range picker when clicking outside
  useEffect(() => {
    const handleOutsideClickForDate = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (datePickerRef.current && !datePickerRef.current.contains(target)) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener('mousedown', handleOutsideClickForDate);
      document.addEventListener('touchstart', handleOutsideClickForDate);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClickForDate);
      document.removeEventListener('touchstart', handleOutsideClickForDate);
    };
  }, [showDatePicker]);
  const handleStatsSelect = (_value: string, label: string) => {
    setSelectedStatsOption(label);
    setStatsDropdownOpen(false);
  };
  const handleDateRangeApply = (
    startDate: Date | null,
    endDate: Date | null
  ) => {
    setDateRange({
      start: startDate ? startDate.toISOString() : null,
      end: endDate ? endDate.toISOString() : null,
    });
    // Clear timeframe when custom date range is selected
    if (startDate && endDate) {
      setSelectedTimeframe("");
    }
    setShowDatePicker(false);
  };
  const handleDateRangeCancel = () => {
    setShowDatePicker(false);
  };
  // Quick Access BarChart items
  const barChartItems = [
    {
      name: "Help Center",
      clicks: quickAccessCounts.helpCenter,
      color: "bg-[#F15D18]",
    },
    {
      name: "Support Chat",
      clicks: quickAccessCounts.supportChat,
      color: "bg-[#F58957]",
    },
    {
      name: "Video Tutorials",
      clicks: quickAccessCounts.videoTutorials,
      color: "bg-[#9F390A]",
    },
    {
      name: "Tooltip Package",
      clicks: quickAccessCounts.tooltipPackage,
      color: "bg-[#919191]",
    },
  ];
  return (
    <Layout>
      <div
        className="min-h-screen bg-white rounded-xl"
        style={{
          boxShadow: " 0px 2px 2px 0px #00000040",
        }}
      >
        {/* Header */}
        {/* Main Content */}
        <div className="p-6">
          <div className="mb-6 flex justify-between">
            <div>
              <h1 className="text-[20px] nunito-semibold text-[#696969]">
                Dashboard
              </h1>
              <p className="text-[#919191] nunito-regular mt-1">
                Hi {profileData?.name}, Welcome to Supporty.io! Let’s get you
                started!
              </p>
              <p className="text-[#919191] nunito-regular">
                And whenever you need,you can always come back here to track all
                your key activities in one place.
              </p>
            </div>{" "}
            {user?.teamRole !== "customer_team_member" && (
              <div className="">
                <label className="text-[#696969] mr-1  text-[20px] nunito-semibold">
                  Stats for:
                </label>
                <div className="relative inline-block group">
                  <button
                    onClick={() => setStatsDropdownOpen(!statsDropdownOpen)}
                    disabled
                    className="h-[40px] w-[280px] border-[#A8A8A8] border-[1px] size-[16px] text-[#696969] nunito-semibold rounded-[8px] mx-3 px-3 flex items-center justify-between"
                  >
                    {selectedStatsOption || "Me"}
                    {statsDropdownOpen ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                  {/* Tooltip on hover */}
                  <div className="absolute left-1/2 -translate-x-1/2 -top-8 px-2 py-1 bg-[#FAFAFA] nunito-regular text-[12px] text-[#696969] text-xs rounded-[8px] shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 whitespace-nowrap z-30">
                    This feature is on its way - stay tuned!
                  </div>
                  {statsDropdownOpen && (
                    <div className="absolute left-3 right-0 mt-1 w-[280px] bg-white border border-[#A8A8A8] rounded-[8px] z-20">
                      {statsOptions.map((option) => (
                        <div
                          key={option.value}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-[#696969] nunito-semibold"
                          onClick={() =>
                            handleStatsSelect(option.value, option.label)
                          }
                        >
                          {option.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {/* Download Extension button */}
          <DownloadExtension />
          {/* Hero Section */}
          <QuickSupportCard />
          {user?.teamRole !== "customer_team_member" && currentTeam == null && (
            <SetUpTeamCard user={user} />
          )}
          {/* Timeframe Selection */}
          <div className="flex justify-end gap-4 mt-4 mb-4 relative">
            <div className="relative" ref={timeframeRef}>
              <button
                onClick={() => setTimeframeDropdownOpen(!timeframeDropdownOpen)}
                className="text-[#404040] Roboto-regular w-[150px] text-[16px]  py-1 flex items-center gap-2  rounded"
              >
                {selectedTimeframe
                  ? timeframeOptions.find(
                      (opt) => opt.value === selectedTimeframe
                    )?.label
                  : "Select timeframe"}
                {timeframeDropdownOpen ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
              {timeframeDropdownOpen && (
                <div className="absolute left-0 top-full text-[#404040] sixe-[16px] nunito-regular z-10 mt-1 bg-white shadow-md rounded w-full">
                  {" "}
                  {timeframeOptions.map((option) => (
                    <div
                      key={option.value}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleTimeframeSelect(option.value)}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>{" "}
            <div className="relative" ref={datePickerRef}>
              <button
                onClick={() => setShowDatePicker(!showDatePicker)}
                className="flex items-center text-[#404040] Roboto-regular text-[16px] gap-2 px-3 py-1"
              >
                {dateRange.start && dateRange.end
                  ? `${new Date(dateRange.start).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })} - ${new Date(dateRange.end).toLocaleDateString(
                      "en-US",
                      {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      }
                    )}`
                  : "Custom range"}
                {showDatePicker ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
              {showDatePicker && (
                <div className="absolute right-0 top-full z-10 mt-1">
                  <DateRangePicker
                    onApply={handleDateRangeApply}
                    onCancel={handleDateRangeCancel}
                  />
                </div>
              )}
            </div>
          </div>
          <hr className="text-[#EBEBEB] border-t border-[3px]" />{" "}
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5 mb-8">
            {" "}
            {/* Interactions Card */}
            <InteractionsCard
              icon={Interaction}
              value={(
                quickAccessCounts.helpCenter +
                quickAccessCounts.supportChat +
                quickAccessCounts.videoTutorials +
                quickAccessCounts.tooltipPackage +
                totalChatSessions +
                filteredActivities.length
              ).toString()}
              title="Interactions"
              details={[
                {
                  icon: () => (
                    <img
                      src={QuickAccess}
                      alt="QuickAccess"
                      className="w-[24px] h-[24px] stroke-[4px]"
                    />
                  ),
                  label: "Quick Access",
                  value:
                    quickAccessCounts.helpCenter +
                    quickAccessCounts.supportChat +
                    quickAccessCounts.videoTutorials +
                    quickAccessCounts.tooltipPackage,
                },
                {
                  icon: () => (
                    <img
                      src={QuickAnswer}
                      alt="QuickAnswer"
                      className="w-[24px] h-[24px] stroke-[4px]"
                    />
                  ),
                  label: "Quick Answers",
                  value: totalChatSessions.toString(),
                },
                {
                  icon: () => (
                    <img
                      src={ExperttieSmall}
                      alt="QuickExpert"
                      className="w-[24px] h-[24px] stroke-[4px]"
                    />
                  ),
                  label: "Quick Experts",
                  value: filteredActivities.length.toString(),
                },
              ]}
            />{" "}
            {/* Amount Card */}
            <InteractionsCard
              icon={AmountSpend}
              value={`$${(totalSessionsAmount + totalTooltipPurchasesAmount).toFixed(2)}`}
              title="Amount Spent"
              details={[
                {
                  icon: () => (
                    <img
                      src={videoIconSmall}
                      alt="videoIconSmall"
                      className="w-[24px] h-[24px] "
                    />
                  ),
                  label: "Expert Sessions",
                  value: `$${totalSessionsAmount.toFixed(2)}`,
                },
                {
                  icon: () => (
                    <img
                      src={tooltipSmall}
                      alt="tooltip"
                      className="w-[24px] h-[24px] "
                    />
                  ),
                  label: "Tooltip Purchases",
                  value: `$${totalTooltipPurchasesAmount.toFixed(2)}`,
                },
              ]}
            />
          </div>
          {/* Charts Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Quick Access Chart */}
            <ChartCard icon={QuickAccess} title="Quick Access" CirculTitle="">
              <BarChart title="No. of Clicks" items={barChartItems} />
            </ChartCard>{" "}
            {/* Question Resolution */}
            <ChartCard icon={QuickAnswer} title="Quick Answers" CirculTitle="">
              <StatsCounter
                items={[{ label: "#Sessions", value: totalChatSessionsWithRating }]}
                className="mt-4 text-[#0A0A0A]"
              />
              <CircularChart
                value={quickAnswersResolutionScore || 0 }
                label="% of sessions with rating 4 or 5"
                circulTitle="Question Resolution"
                showRatings={false}
              />
            </ChartCard>{" "}
            {/* Satisfaction Score */}
            <ChartCard icon={QuickExpert} title="Quick Experts" CirculTitle="">
              <StatsCounter
                items={[
                  { label: "#Sessions", value: filteredActivities.length },
                ]}
                className="mt-4 text-[#0A0A0A]"
              />

              <CircularChart
                value={satisfactionScore}
                label="% of sessions with rating 4 or 5"
                circulTitle="Satisfaction Score"
                showRatings={false}
              />
            </ChartCard>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CustomerDashboard;
